<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <i class="fas fa-star text-warning me-2"></i>
        <h2 class="mb-0">{{ $t('reviewAnalytics.adataReviewAnalytics') }}</h2>
      </div>
      <!-- <div class="d-flex gap-2">
        <button class="btn btn-outline-primary" @click="activeTab = 'overview'">
          <i class="fas fa-chart-bar me-1"></i>Overview
        </button>
        <button class="btn btn-primary" @click="refreshData" :disabled="loading">
          <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i>
          {{ loading ? 'Loading...' : 'Refresh Data' }}
        </button>
      </div> -->
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ $t('reviewAnalytics.authenticationRequired') }}
      <button type="button" class="btn-close" @click="error = null"></button>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('reviewAnalytics.loading') }}</span>
      </div>
      <p class="mt-3 text-muted">{{ $t('reviewAnalytics.loadingAdataReviewAnalytics') }}</p>
    </div>

    <!-- Main Content (only show when not loading) -->
    <div v-if="!loading">
      <!-- Review Summary Cards -->
      <div class="row mb-4">
      <div class="col-md-3 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <div class="card-body text-white text-center">
            <h2 class="mb-1">{{ reviewStats.totalReviews.toLocaleString() }}</h2>
            <h6 class="text-white-50 mb-0">{{ $t('reviewAnalytics.totalReviews') }}</h6>
            <small class="text-white-50">+{{ reviewStats.newReviewsThisWeek }} {{ $t('reviewAnalytics.thisWeek') }}</small>
          </div>
        </div>
      </div>
      
      <div class="col-md-3 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #28a745, #20c997);">
          <div class="card-body text-white text-center">
            <h2 class="mb-1">{{ reviewStats.averageRating }}</h2>
            <h6 class="text-white-50 mb-0">{{ $t('reviewAnalytics.averageRating') }}</h6>
            <small class="text-white-50">{{ reviewStats.ratingTrend > 0 ? '+' : '' }}{{ reviewStats.ratingTrend }} {{ $t('reviewAnalytics.vsLastMonth') }}</small>
          </div>
        </div>
      </div>
      
      <div class="col-md-3 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #17a2b8, #007bff);">
          <div class="card-body text-white text-center">
            <h2 class="mb-1">{{ reviewStats.reviewSources }}</h2>
            <h6 class="text-white-50 mb-0">{{ $t('reviewAnalytics.reviewSources') }}</h6>
            <small class="text-white-50">{{ $t('reviewAnalytics.amazonBestBuyNewegg') }}</small>
          </div>
        </div>
      </div>
      
      <div class="col-md-3 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #fd7e14, #ffc107);">
          <div class="card-body text-white text-center">
            <h2 class="mb-1">{{ reviewStats.productsWithReviews }}</h2>
            <h6 class="text-white-50 mb-0">{{ $t('reviewAnalytics.productsAnalyzed') }}</h6>
            <small class="text-white-50">{{ $t('reviewAnalytics.activeMonitoring') }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0">
        <ul class="nav nav-tabs card-header-tabs">
          <li class="nav-item">
            <button class="nav-link" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'">
              <i class="fas fa-chart-bar me-2"></i>{{ $t('reviewAnalytics.overview') }}
            </button>
          </li>
          <li class="nav-item">
            <button class="nav-link" :class="{ active: activeTab === 'products' }" @click="activeTab = 'products'">
              <i class="fas fa-box me-2"></i>{{ $t('reviewAnalytics.productAnalysis') }}
            </button>
          </li>
          <!-- Sentiment Analysis tab temporarily hidden -->
          <!-- <li class="nav-item">
            <button class="nav-link" :class="{ active: activeTab === 'sentiment' }" @click="activeTab = 'sentiment'">
              <i class="fas fa-heart me-2"></i>Sentiment Analysis
            </button>
          </li> -->
          <li class="nav-item">
            <button class="nav-link" :class="{ active: activeTab === 'recent' }" @click="activeTab = 'recent'">
              <i class="fas fa-clock me-2"></i>{{ $t('reviewAnalytics.recentReviews') }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Overview Tab -->
    <div v-if="activeTab === 'overview'">
      <div class="row mb-4">
        <div class="col-lg-6 mb-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
              <h5 class="text-white mb-0">
                <i class="fas fa-chart-pie me-2"></i>{{ $t('reviewAnalytics.reviewsBySource') }}
              </h5>
            </div>
            <div class="card-body">
              <div v-for="source in reviewSources" :key="source.name" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div class="d-flex align-items-center">
                    <i :class="source.icon" class="me-2" :style="{ color: source.color }"></i>
                    <span class="fw-bold">{{ source.name }}</span>
                  </div>
                  <div class="text-end">
                    <span class="fw-bold">{{ source.count.toLocaleString() }}</span>
                    <small class="text-muted ms-2">({{ source.percentage }}%)</small>
                  </div>
                </div>
                <div class="progress">
                  <div class="progress-bar" :style="{ width: source.percentage + '%', backgroundColor: source.color }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-lg-6 mb-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header" style="background: linear-gradient(135deg, #28a745, #20c997);">
              <h5 class="text-white mb-0">
                <i class="fas fa-star me-2"></i>{{ $t('reviewAnalytics.ratingDistribution') }}
              </h5>
            </div>
            <div class="card-body">
              <div v-for="rating in ratingDistribution" :key="rating.stars" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div class="d-flex align-items-center">
                    <span class="me-2">{{ rating.stars }}</span>
                    <div class="text-warning">
                      <i v-for="n in rating.stars" :key="n" class="fas fa-star"></i>
                      <i v-for="n in (5 - rating.stars)" :key="n" class="far fa-star"></i>
                    </div>
                  </div>
                  <div class="text-end">
                    <span class="fw-bold">{{ rating.count.toLocaleString() }}</span>
                    <small class="text-muted ms-2">({{ rating.percentage }}%)</small>
                  </div>
                </div>
                <div class="progress">
                  <div class="progress-bar" :class="getRatingProgressClass(rating.stars)" :style="{ width: rating.percentage + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Analysis Tab -->
    <div v-if="activeTab === 'products'">
      <div class="card border-0 shadow-sm">
        <div class="card-header" style="background: linear-gradient(135deg, #17a2b8, #007bff);">
          <h5 class="text-white mb-0">
            <i class="fas fa-trophy me-2"></i>{{ $t('reviewAnalytics.topRatedProducts') }}
          </h5>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="table-light">
                <tr>
                  <th>{{ $t('reviewAnalytics.rank') }}</th>
                  <th>{{ $t('reviewAnalytics.product') }}</th>
                  <th>{{ $t('reviewAnalytics.brand') }}</th>
                  <th>{{ $t('reviewAnalytics.averageRating') }}</th>
                  <th>{{ $t('reviewAnalytics.totalReviewsCount') }}</th>
                  <!-- <th>Sentiment</th> -->
                  <th>{{ $t('reviewAnalytics.trend') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, index) in topRatedProducts" :key="product.id">
                  <td>
                    <span class="badge" :class="getRankBadgeClass(index + 1)">
                      #{{ index + 1 }}
                    </span>
                  </td>
                  <td>
                    <div>
                      <strong>{{ product.name }}</strong>
                      <br>
                      <small class="text-muted">{{ product.category }}</small>
                    </div>
                  </td>
                  <td>{{ product.brand }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <span class="me-2 fw-bold">{{ product.averageRating }}</span>
                      <div class="text-warning">
                        <i v-for="n in Math.floor(product.averageRating)" :key="n" class="fas fa-star"></i>
                        <i v-if="product.averageRating % 1 !== 0" class="fas fa-star-half-alt"></i>
                        <i v-for="n in (5 - Math.ceil(product.averageRating))" :key="n" class="far fa-star"></i>
                      </div>
                    </div>
                  </td>
                  <td>{{ product.totalReviews.toLocaleString() }}</td>
                  <!-- <td>
                    <span class="badge" :class="getSentimentBadgeClass(product.sentiment)">
                      {{ product.sentiment }}%
                    </span>
                  </td> -->
                  <td>
                    <span :class="getTrendClass(product.trend)">
                      <i :class="getTrendIcon(product.trend)" class="me-1"></i>
                      {{ Math.abs(product.trend) }}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Sentiment Analysis Tab - Temporarily Hidden -->
    <!-- <div v-if="activeTab === 'sentiment'">
      <div class="row">
        <div class="col-lg-8 mb-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header" style="background: linear-gradient(135deg, #e83e8c, #fd7e14);">
              <h5 class="text-white mb-0">
                <i class="fas fa-heart me-2"></i>Sentiment Analysis by Brand
              </h5>
            </div>
            <div class="card-body">
              <div v-for="brand in brandSentiment" :key="brand.name" class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">{{ brand.name }}</h6>
                  <span class="badge bg-info">{{ brand.totalReviews }} reviews</span>
                </div>
                
                <div class="row">
                  <div class="col-md-4 mb-2">
                    <small class="text-success">{{ t('reviewAnalytics.positive') }} ({{ brand.positive }}%)</small>
                    <div class="progress">
                      <div class="progress-bar bg-success" :style="{ width: brand.positive + '%' }"></div>
                    </div>
                  </div>
                  <div class="col-md-4 mb-2">
                    <small class="text-warning">{{ t('reviewAnalytics.neutral') }} ({{ brand.neutral }}%)</small>
                    <div class="progress">
                      <div class="progress-bar bg-warning" :style="{ width: brand.neutral + '%' }"></div>
                    </div>
                  </div>
                  <div class="col-md-4 mb-2">
                    <small class="text-danger">{{ t('reviewAnalytics.negative') }} ({{ brand.negative }}%)</small>
                    <div class="progress">
                      <div class="progress-bar bg-danger" :style="{ width: brand.negative + '%' }"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-lg-4 mb-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-success text-white">
              <h6 class="mb-0">
                <i class="fas fa-thumbs-up me-2"></i>Key Insights
              </h6>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <h6>Most Praised Features</h6>
                <div class="d-flex flex-wrap gap-1">
                  <span v-for="feature in praisedFeatures" :key="feature" class="badge bg-success">
                    {{ feature }}
                  </span>
                </div>
              </div>
              
              <div class="mb-3">
                <h6>Common Complaints</h6>
                <div class="d-flex flex-wrap gap-1">
                  <span v-for="complaint in commonComplaints" :key="complaint" class="badge bg-danger">
                    {{ complaint }}
                  </span>
                </div>
              </div>
              
              <div>
                <h6>Improvement Areas</h6>
                <ul class="list-unstyled mb-0">
                  <li v-for="area in improvementAreas" :key="area" class="mb-1">
                    <i class="fas fa-arrow-right text-primary me-2"></i>
                    {{ area }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div> -->

    <!-- Recent Reviews Tab -->
    <div v-if="activeTab === 'recent'">
      <div class="card border-0 shadow-sm">
        <div class="card-header" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <h5 class="text-white mb-0">
            <i class="fas fa-clock me-2"></i>{{ $t('reviewAnalytics.recentReviews') }}
          </h5>
        </div>
        <div class="card-body">
          <div v-for="review in recentReviews" :key="review.id" class="border rounded p-3 mb-3">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="flex-grow-1">
                <div class="d-flex align-items-center mb-2">
                  <div class="text-warning me-2">
                    <i v-for="n in review.rating" :key="n" class="fas fa-star"></i>
                    <i v-for="n in (5 - review.rating)" :key="n" class="far fa-star"></i>
                  </div>
                  <span class="badge" :class="getSourceBadgeClass(review.source)">
                    {{ review.source }}
                  </span>
                  <!-- <span class="badge ms-2" :class="getSentimentBadgeClass(review.sentimentScore)">
                    {{ getSentimentLabel(review.sentimentScore) }}
                  </span> -->
                </div>
                
                <h6 class="mb-2">{{ review.productName }}</h6>
                <p class="mb-2">{{ review.reviewText }}</p>
                
                <div class="d-flex justify-content-between align-items-center">
                  <small class="text-muted">
                    {{ $t('reviewAnalytics.by') }} {{ review.reviewerName }} | {{ formatDate(review.reviewDate) }}
                  </small>
                  <div>
                    <span class="badge bg-light text-dark me-2">
                      <i class="fas fa-thumbs-up me-1"></i>{{ review.helpfulVotes }}
                    </span>
                    <!-- <span class="badge bg-light text-dark">
                      {{ review.verifiedPurchase ? $t('reviewAnalytics.verified') : $t('reviewAnalytics.unverified') }}
                    </span> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div> <!-- End of main content wrapper -->
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import adataReviewService from '../services/adataReviewService.js';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const activeTab = ref('overview');
const loading = ref(false);
const error = ref(null);

// Review statistics - 初始化為空，將從 API 載入
const reviewStats = ref({
  totalReviews: 0,
  averageRating: '0.0',
  reviewSources: 0,
  productsWithReviews: 0,
  newReviewsThisWeek: 0,
  ratingTrend: 0,
  sentimentScore: 0  // 固定為 0
});

// Review sources data - 將從 API 載入
const reviewSources = ref([]);

// Rating distribution - 將從 API 載入
const ratingDistribution = ref([]);

// Top rated products - 將從 API 載入
const topRatedProducts = ref([]);

// Brand sentiment analysis - 將從 API 載入
const brandSentiment = ref([]);

// Key insights - 將從 API 載入
const praisedFeatures = ref([]);
const commonComplaints = ref([]);
const improvementAreas = ref([]);

// Recent reviews - 將從 API 載入
const recentReviews = ref([]);

// Methods
const getRatingProgressClass = (stars) => {
  if (stars >= 4) return 'bg-success';
  if (stars >= 3) return 'bg-warning';
  return 'bg-danger';
};

const getRankBadgeClass = (rank) => {
  if (rank === 1) return 'bg-warning text-dark';
  if (rank <= 3) return 'bg-info';
  return 'bg-secondary';
};

// getSentimentBadgeClass removed - not used after hiding sentiment analysis

const getTrendClass = (trend) => {
  return trend > 0 ? 'text-success' : 'text-danger';
};

const getTrendIcon = (trend) => {
  return trend > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down';
};

const getSourceBadgeClass = (source) => {
  switch (source) {
    case 'Amazon':
      return 'bg-warning text-dark';
    case 'BestBuy':
      return 'bg-primary';
    case 'Newegg':
      return 'bg-info';
    default:
      return 'bg-secondary';
  }
};

// getSentimentLabel removed - not used after hiding sentiment analysis

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// const refreshData = async () => {
//   console.log('Refreshing ADATA review analytics data...');
//   await fetchReviewStats();
// };

const fetchReviewStats = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    console.log('🚀 Loading ADATA review analytics data...');
    
    // 載入所有資料
    const data = await adataReviewService.loadAllData();
    console.log('📊 Loaded data:', data);
    
    // 更新統計資料
    reviewStats.value = {
      totalReviews: data.summary.total_reviews || 0,
      averageRating: (data.summary.average_rating || 0).toFixed(1),
      reviewSources: data.summary.review_sources || 0,
      productsWithReviews: data.summary.products_with_reviews || 0,
      newReviewsThisWeek: data.summary.new_reviews_this_week || 0,
      ratingTrend: data.summary.rating_trend || 0,
      sentimentScore: data.summary.sentiment_score || 0  // 應該是 0
    };
    
    // 更新評論來源
    reviewSources.value = data.sources || [];
    
    // 更新評分分佈
    ratingDistribution.value = data.ratingDistribution || [];
    
    // 更新品牌情感分析
    brandSentiment.value = data.brandSentiment || [];
    
    // 更新洞察資料
    if (data.insights) {
      praisedFeatures.value = data.insights.praised_features || [];
      commonComplaints.value = data.insights.common_complaints || [];
      improvementAreas.value = data.insights.improvement_areas || [];
    }
    
    // 更新熱門產品
    topRatedProducts.value = (data.topProducts || []).map(product => ({
      id: product.id,
      name: product.name,
      brand: product.brand,
      category: product.category,
      averageRating: product.average_rating || 0,
      totalReviews: product.total_reviews || 0,
      sentiment: product.sentiment || 0,  // 應該是 0
      trend: product.trend || 0  // 應該是 0
    }));
    
    // 更新最新評論
    recentReviews.value = (data.recentReviews || []).map(review => ({
      id: review.id,
      productName: review.product_name,
      rating: review.rating,
      reviewText: review.review_text,
      reviewerName: review.reviewer_name,
      reviewDate: review.review_date,
      source: review.source,
      sentimentScore: review.sentiment_score || 0,  // 應該是 0
      helpfulVotes: review.helpful_votes || 0,
      verifiedPurchase: review.verified_purchase || false
    }));
    
    console.log('✅ ADATA review analytics data loaded successfully');
    console.log('📈 Stats:', reviewStats.value);
    console.log('📊 Sources:', reviewSources.value.length);
    console.log('⭐ Rating Distribution:', ratingDistribution.value.length);
    
  } catch (error) {
    console.error('❌ Failed to fetch ADATA review stats:', error);
    
    // 更詳細的錯誤處理
    if (error.response) {
      // API 回應錯誤
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.statusText;
      
      if (status === 401) {
        error.value = t('reviewAnalytics.authenticationRequired');
      } else if (status === 403) {
        error.value = t('reviewAnalytics.accessDenied');
      } else if (status >= 500) {
        error.value = t('reviewAnalytics.serverError');
      } else {
        error.value = `${t('reviewAnalytics.apiError')} ${message}`;
      }
    } else if (error.request) {
      // 網路錯誤
      error.value = t('reviewAnalytics.networkError');
    } else {
      // 其他錯誤
      error.value = t('reviewAnalytics.failedToLoadReviewAnalyticsData');
    }
    
    // 如果 API 失敗，設置預設值
    if (reviewStats.value.totalReviews === 0) {
      reviewStats.value = {
        totalReviews: 0,
        averageRating: '0.0',
        reviewSources: 0,
        productsWithReviews: 0,
        newReviewsThisWeek: 0,
        ratingTrend: 0,
        sentimentScore: 0
      };
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchReviewStats();
});
</script>

<style scoped>
.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.opacity-50 {
  opacity: 0.5;
}
</style>
